import React, { useState } from 'react';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { EditionForm } from "./EditionForm";
import { Tables } from "@/integrations/supabase/types";
import { Media } from "./types";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";

interface EditionTableProps {
  artwork_id: number;
  onSelectEdition: (edition: {
    edition_id: number;
    print_price: number | null;
    default_media?: number | null;
  } | null) => void;
  mediaTypes: Media[];
}

type Edition = Tables<"editions">;

export function EditionTable({ artwork_id, onSelectEdition, mediaTypes }: EditionTableProps) {
  const [selectedEdition, setSelectedEdition] = useState<Edition | null>(null);
  const queryClient = useQueryClient();

  const { data: editions } = useQuery({
    queryKey: ["editions", artwork_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("editions")
        .select("*")
        .eq("artwork_id", artwork_id);

      if (error) throw error;
      return data as Edition[];
    },
  });

  const handlePrintPriceChange = (value: number) => {
    if (selectedEdition) {
      setSelectedEdition({
        ...selectedEdition,
        print_price: value
      });
    }
  };

  const handleMediaTypeChange = (value: string) => {
    if (selectedEdition) {
      setSelectedEdition({
        ...selectedEdition,
        default_media: parseInt(value)
      });
    }
  };

  const handleCertifiedChange = (value: boolean) => {
    if (selectedEdition) {
      setSelectedEdition({
        ...selectedEdition,
        certified: value
      });
    }
  };

  const handleSaveEdition = async () => {
    if (!selectedEdition) return;

    const { error } = await supabase
      .from('editions')
      .update({
        print_price: selectedEdition.print_price,
        default_media: selectedEdition.default_media,
        certified: selectedEdition.certified
      })
      .eq('edition_id', selectedEdition.edition_id);

    if (error) {
      console.error('Error updating edition:', error);
      return;
    }

    setSelectedEdition(null);
    queryClient.invalidateQueries({ 
      queryKey: ['editions', artwork_id] 
    });
  };

  return (
    <div className="space-y-4">
      <table className="w-full">
        <thead>
          <tr>
            <th className="text-left">Edition Name</th>
            <th className="text-left">Size</th>
            <th className="text-left">Price</th>
            <th className="text-left">Print Price</th>
            <th className="text-left">Default Media</th>
            <th className="text-left">Certified</th>
            <th className="text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {editions?.map((edition, index) => (
            <tr 
              key={edition.edition_id}
              className={index % 2 === 1 ? "bg-gray-50" : ""}
            >
              <td>{edition.edition_name}</td>
              <td>{`${edition.width} x ${edition.height}`}</td>
              <td>{edition.price}</td>
              <td>{edition.print_price}</td>
              <td>
                {mediaTypes.find(m => m.media_id === edition.default_media)?.media_name || '-'}
              </td>
              <td>{edition.certified ? "Yes" : "No"}</td>
              <td>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={() => setSelectedEdition(edition)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={!!selectedEdition} onOpenChange={(open) => !open && setSelectedEdition(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Edition Details</DialogTitle>
          </DialogHeader>
          <EditionForm
            selectedEdition={selectedEdition}
            mediaTypes={mediaTypes}
            onCancel={() => setSelectedEdition(null)}
            onSubmit={handleSaveEdition}
            onPrintPriceChange={handlePrintPriceChange}
            onMediaTypeChange={handleMediaTypeChange}
            onCertifiedChange={handleCertifiedChange}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}